import { useEffect, useRef, useState } from 'react'
import { messagesApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'

interface Thread {
  seller_id: number
  seller_name: string
  last_message: string | null
  last_message_at: string | null
  unread_count: number
}

interface Message {
  id: number
  sender_id: number
  body: string
  created_at: string
  read_at: string | null
}

const POLL_INTERVAL_MS = 7000

export default function MessagesPage() {
  const { user } = useAuth()
  const [threads, setThreads] = useState<Thread[]>([])
  const [threadsLoading, setThreadsLoading] = useState(true)
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const fetchThreads = async () => {
    try {
      const res = await messagesApi.threads()
      setThreads(res.data.threads)
    } catch {
      setError('No se pudieron cargar las conversaciones')
    } finally {
      setThreadsLoading(false)
    }
  }

  const fetchMessages = async (sellerId: number) => {
    try {
      const res = await messagesApi.list(sellerId)
      setMessages(res.data.messages)
    } catch {
      setError('No se pudieron cargar los mensajes')
    }
  }

  useEffect(() => {
    fetchThreads()
    const interval = setInterval(fetchThreads, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (selectedSellerId === null) return
    setMessagesLoading(true)
    fetchMessages(selectedSellerId).finally(() => setMessagesLoading(false))
    const interval = setInterval(() => fetchMessages(selectedSellerId), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [selectedSellerId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSelectThread = (sellerId: number) => {
    setSelectedSellerId(sellerId)
    setError('')
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim() || selectedSellerId === null) return
    setSending(true)
    setError('')
    try {
      await messagesApi.send(selectedSellerId, text.trim())
      setText('')
      await fetchMessages(selectedSellerId)
      fetchThreads()
    } catch {
      setError('No se pudo enviar el mensaje')
    } finally {
      setSending(false)
    }
  }

  const adminId = user ? Number(user.user_id) : null
  const selectedThread = threads.find((t) => t.seller_id === selectedSellerId)

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Mensajes</h1>
      <div className="bg-cream-50 shadow rounded-lg flex h-[70vh] overflow-hidden">
        {/* Thread list */}
        <div className="w-64 border-r border-espresso-200 overflow-y-auto">
          {threadsLoading ? (
            <div className="text-center text-espresso-500 py-8 text-sm">Cargando...</div>
          ) : threads.length === 0 ? (
            <div className="text-center text-espresso-500 py-8 text-sm px-4">No hay vendedores activos</div>
          ) : (
            threads.map((t) => (
              <button
                key={t.seller_id}
                onClick={() => handleSelectThread(t.seller_id)}
                className={`w-full text-left px-4 py-3 border-b border-espresso-100 hover:bg-cream-50 ${
                  t.seller_id === selectedSellerId ? 'bg-terracotta-50' : ''
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-espresso-800">{t.seller_name}</span>
                  {t.unread_count > 0 && (
                    <span className="bg-terracotta-600 text-white text-xs rounded-full px-2 py-0.5">
                      {t.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-xs text-espresso-500 truncate mt-1">
                  {t.last_message || 'Sin mensajes todavía'}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Conversation */}
        <div className="flex-1 flex flex-col">
          {selectedSellerId === null ? (
            <div className="flex-1 flex items-center justify-center text-espresso-500 text-sm">
              Selecciona un vendedor para ver la conversación
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-espresso-200 font-medium text-espresso-800">
                {selectedThread?.seller_name}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesLoading ? (
                  <div className="text-center text-espresso-500 py-8">Cargando...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-espresso-500 py-8">Todavía no hay mensajes</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === adminId
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-md px-4 py-2 rounded-lg text-sm ${
                            mine ? 'bg-terracotta-600 text-white' : 'bg-cream-100 text-espresso-800'
                          }`}
                        >
                          <div>{m.body}</div>
                          <div className={`text-xs mt-1 ${mine ? 'text-terracotta-100' : 'text-espresso-500'}`}>
                            {new Date(m.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={bottomRef} />
              </div>
              {error && <div className="px-4 py-2 text-sm text-red-600 border-t border-espresso-100">{error}</div>}
              <form onSubmit={handleSend} className="border-t border-espresso-200 p-4 flex gap-2">
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  maxLength={2000}
                  className="flex-1 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  Enviar
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
